/**
 * TanStack Query hooks for user administration (Settings → Users).
 *
 * These call OUR /api/users endpoints, not `authClient.admin.*` directly. The admin
 * plugin's endpoints write the user row with no audit trail, and a role or access change
 * is the event most worth being able to look up afterwards — so the server handlers wrap
 * them and record what happened. See src/api/users.ts.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { jsonFetch } from "@/lib/json-fetch";
import type { NymblRole } from "@/lib/nymbl-roles";
import { NYMBL_ROLES } from "@/lib/nymbl-roles";
import { userKeys, userRoleRuleKeys } from "@/lib/query";
import type {
  AccessStatus,
  OwnedAccountRow,
  OwnershipSuggestion,
  RoleRuleRow,
  UserDetail,
  UserListPage,
} from "@/schemas/users";

export type {
  AccessStatus,
  AuthProvider,
  OwnedAccountRow,
  OwnershipSuggestion,
  RoleRuleRow,
  RoleSource,
  UserDetail,
  UserListPage,
  UserRow,
} from "@/schemas/users";

/** Roles the admin UI can assign — the §5 Nymbl Staff roles (see @/lib/nymbl-roles). */
export const USER_ROLES = NYMBL_ROLES;
export type UserRole = NymblRole;

export function isUserRole(value: string): value is UserRole {
  return (USER_ROLES as readonly string[]).includes(value);
}

/** What each role is for, shown beside the picker so a role change is an informed one. */
export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  ADMIN:
    "Sees the whole portfolio; manages configuration, users and Resource attributes. No staffing writes.",
  RM: "Owns staffing decisions across the book — the broadest role.",
  SA: "Delivery. Their own profile, their own Solutions, their own ratings.",
  ACCT: "Account Manager. Everything is scoped to the Accounts they are given below.",
  FIN: "Finance. Rate Cards, cost, utilisation, and plan approvals.",
};

// ── Query params ─────────────────────────────────────────────────────────────

export interface UserListParams extends Record<string, unknown> {
  cursor?: string;
  direction?: "next" | "prev";
  search?: string;
  role?: string;
  provider?: string;
  status?: string;
  limit?: number;
}

function queryString(params: UserListParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "" && value !== "all") search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

// ── Reads ────────────────────────────────────────────────────────────────────

export function useUsers(params: UserListParams = {}) {
  return useQuery({
    queryKey: userKeys.list(params),
    queryFn: () => jsonFetch<UserListPage>(`/api/users${queryString(params)}`),
    // Paging and filtering both re-key; without this the table empties between pages.
    placeholderData: keepPreviousData,
  });
}

/** The detail sheet fetches fresh when opened — never pass a stale row in as props. */
export function useUserDetail(userId: string | null) {
  return useQuery({
    queryKey: userKeys.detail(userId ?? ""),
    queryFn: () => jsonFetch<UserDetail>(`/api/users/${userId}`),
    enabled: userId !== null,
  });
}

export function useOwnedAccounts(userId: string | null) {
  return useQuery({
    queryKey: userKeys.accounts(userId ?? ""),
    queryFn: () => jsonFetch<OwnedAccountRow[]>(`/api/users/${userId}/accounts`),
    enabled: userId !== null,
  });
}

export function useOwnershipSuggestions(userId: string | null) {
  return useQuery({
    queryKey: userKeys.suggestions(userId ?? ""),
    queryFn: () => jsonFetch<OwnershipSuggestion[]>(`/api/users/${userId}/accounts/suggestions`),
    enabled: userId !== null,
  });
}

export function useRoleRules() {
  return useQuery({
    queryKey: userRoleRuleKeys.list(),
    queryFn: () => jsonFetch<RoleRuleRow[]>("/api/user-role-rules"),
  });
}

// ── Mutation plumbing ────────────────────────────────────────────────────────

/**
 * Every user mutation invalidates the same two things: the list (counts and badges move)
 * and that user's detail. Centralised so a new mutation cannot forget one.
 */
function useUserMutation<TInput>(
  request: (input: TInput) => Promise<unknown>,
  options: { success: string; failure: string; userIdOf?: (input: TInput) => string },
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      const userId = options.userIdOf?.(input);
      if (userId) queryClient.invalidateQueries({ queryKey: userKeys.detail(userId) });
      toast.success(options.success);
    },
    onError: (error: Error) => {
      toast.error(error.message || options.failure);
    },
  });
}

// ── Writes ───────────────────────────────────────────────────────────────────

export function useCreateUser() {
  return useUserMutation<{ name: string; email: string; password: string; role: UserRole }>(
    (input) =>
      jsonFetch("/api/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
    { success: "User created", failure: "Failed to create user" },
  );
}

export function useChangeRole() {
  return useUserMutation<{ userId: string; role: UserRole; reason: string }>(
    ({ userId, role, reason }) =>
      jsonFetch(`/api/users/${userId}/role`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role, reason }),
      }),
    { success: "Role updated", failure: "Failed to change role", userIdOf: (i) => i.userId },
  );
}

export function useResetRoleSource() {
  return useUserMutation<{ userId: string }>(
    ({ userId }) => jsonFetch(`/api/users/${userId}/role-override`, { method: "DELETE" }),
    {
      success: "Role handed back to automatic",
      failure: "Failed to reset the role",
      userIdOf: (i) => i.userId,
    },
  );
}

export function useSetAccess() {
  return useUserMutation<{
    userId: string;
    status: AccessStatus;
    reason?: string;
    expiresAt?: string;
  }>(
    ({ userId, ...body }) =>
      jsonFetch(`/api/users/${userId}/access`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    {
      success: "Access updated",
      failure: "Failed to update access",
      userIdOf: (i) => i.userId,
    },
  );
}

export function useRevokeSessions() {
  return useUserMutation<{ userId: string }>(
    ({ userId }) => jsonFetch(`/api/users/${userId}/sessions`, { method: "DELETE" }),
    {
      success: "Signed out of every device",
      failure: "Failed to revoke sessions",
      userIdOf: (i) => i.userId,
    },
  );
}

export function useResetPassword() {
  return useUserMutation<{ userId: string; newPassword: string }>(
    ({ userId, newPassword }) =>
      jsonFetch(`/api/users/${userId}/password`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newPassword }),
      }),
    { success: "Password reset", failure: "Failed to reset password", userIdOf: (i) => i.userId },
  );
}

export function useDeleteUser() {
  return useUserMutation<{ userId: string }>(
    ({ userId }) => jsonFetch(`/api/users/${userId}`, { method: "DELETE" }),
    { success: "User deleted", failure: "Failed to delete user" },
  );
}

// ── Account ownership ────────────────────────────────────────────────────────

function useOwnershipMutation<TInput extends { userId: string }>(
  request: (input: TInput) => Promise<unknown>,
  messages: { success: string; failure: string },
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({ queryKey: userKeys.accounts(input.userId) });
      queryClient.invalidateQueries({ queryKey: userKeys.suggestions(input.userId) });
      toast.success(messages.success);
    },
    onError: (error: Error) => {
      toast.error(error.message || messages.failure);
    },
  });
}

export function useGrantAccountOwnership() {
  return useOwnershipMutation<{ userId: string; clientAccountId: string }>(
    ({ userId, clientAccountId }) =>
      jsonFetch(`/api/users/${userId}/accounts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientAccountId }),
      }),
    { success: "Account assigned", failure: "Failed to assign the Account" },
  );
}

export function useRevokeAccountOwnership() {
  return useOwnershipMutation<{ userId: string; clientAccountId: string }>(
    ({ userId, clientAccountId }) =>
      jsonFetch(`/api/users/${userId}/accounts/${clientAccountId}`, { method: "DELETE" }),
    { success: "Account removed", failure: "Failed to remove the Account" },
  );
}

// ── Role-mapping rules ───────────────────────────────────────────────────────

function useRoleRuleMutation<TInput>(
  request: (input: TInput) => Promise<unknown>,
  messages: { success: string; failure: string },
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userRoleRuleKeys.all });
      // A rule change moves what the detail sheet previews for "reset to automatic".
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success(messages.success);
    },
    onError: (error: Error) => {
      toast.error(error.message || messages.failure);
    },
  });
}

export interface RoleRuleInput {
  matchType: "email" | "domain";
  matchValue: string;
  role: UserRole;
  priority: number;
  active: boolean;
  notes?: string | null;
}

export function useCreateRoleRule() {
  return useRoleRuleMutation<RoleRuleInput>(
    (input) =>
      jsonFetch("/api/user-role-rules", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
    { success: "Rule added", failure: "Failed to add the rule" },
  );
}

export function useUpdateRoleRule() {
  return useRoleRuleMutation<{ id: string } & Partial<RoleRuleInput>>(
    ({ id, ...body }) =>
      jsonFetch(`/api/user-role-rules/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    { success: "Rule updated", failure: "Failed to update the rule" },
  );
}

export function useDeleteRoleRule() {
  return useRoleRuleMutation<{ id: string }>(
    ({ id }) => jsonFetch(`/api/user-role-rules/${id}`, { method: "DELETE" }),
    { success: "Rule removed", failure: "Failed to remove the rule" },
  );
}
